/**
 * Budjet -> Carrier to'lovlari: ishga olingan har driver uchun carrier kompaniyaga to'laydigan pul (mijoz jadvalidagi
 * "Payment", muddat = ishga chiqqan kun + N). Holat qo'lda emas -- Pul daftaridagi tushumlardan (kutilmoqda / qisman /
 * to'langan); "to'lanmadi" -- yozib yuborish. Pul qabul qilish har doim oyna orqali (hisob majburiy) va faqat tasdiqlangan
 * hire uchun. Filtrlar URL'da, standart -- hammasi.
 */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useMemo, useState, type MouseEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { MoreHorizontal, Plus } from "lucide-react";
import { can } from "@/features/auth/types";
import { payError, useBudgetBranches, usePlacements, useUpdatePlacementFee, useVoidFeePayment } from "@/features/budget/api";
import { placementMenu } from "@/features/budget/menu";
import { money, type PlacementFee } from "@/features/budget/types";
import { useCarrierOptions } from "@/features/carriers/api";
import { useAppSettings, useUserOptions } from "@/features/drivers/api";
import { usePersonNav } from "@/features/people/usePersonNav";
import { copyText } from "@/lib/clipboard";
import { dayjs, formatDate, formatMoney, formatMoneyExact } from "@/lib/utils";
import { PersonLink } from "@/components/PersonLink";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/ui/card";
import { confirmDialog } from "@/components/ui/confirm";
import { ContextMenu } from "@/components/ui/context-menu";
import { FilterSelect, Input } from "@/components/ui/field";
import { EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { BudgetLayout, scopeLabel, useBudgetParams } from "./BudgetLayout";
import { canReceive, FeeBadge, FeeEditModal, PaymentModal, toFeeTarget, type FeeTarget } from "./FeeParts";
import { fmt, whenText } from "@/features/finance/money";

const STATES = ["all", "open", "overdue", "due", "paid", "partial", "not_paid"] as const;
type State = (typeof STATES)[number];
// `month` -- ishga chiqqan oy; Budjet tablari URL'da `period` ni (ko'rinish oyi) saqlaydi, u bilan aralashmasin
const FILTER_KEYS = ["state", "carrier", "owner", "approved", "month"] as const;

/** Ishga chiqqan oylar: 9 oy orqaga, 3 oy oldinga (kelajakdagi start ham bo'ladi) */
function monthOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let i = 3; i >= -9; i--) {
    const d = dayjs().add(i, "month");
    out.push({ value: d.format("YYYY-MM"), label: d.format("MMM YYYY") });
  }
  return out;
}

export function PlacementsPage() {
  const { t } = useTranslation("budget");
  const { t: ti } = useTranslation("metrics");
  const { t: tc } = useTranslation();
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const personNav = usePersonNav();
  const { branch, me, showBranchColumn } = useBudgetParams();
  const branches = useBudgetBranches();
  const [params, setParams] = useSearchParams();
  const get = (k: string) => params.get(k) ?? "";
  const state: State = (STATES as readonly string[]).includes(get("state")) ? (get("state") as State) : "all";
  const page = Math.max(1, Number(get("page") || 1));
  const pageSize = urlPageSize(get("page_size"), "placements", 50);
  const [q, setQ] = useState("");
  const [paying, setPaying] = useState<FeeTarget | null>(null);
  const [editing, setEditing] = useState<FeeTarget | null>(null);
  const update = useUpdatePlacementFee();
  const voidPayment = useVoidFeePayment();
  const settings = useAppSettings();
  const carriers = useCarrierOptions();
  const owners = useUserOptions({ role: "recruiter" });
  const months = useMemo(monthOptions, []);
  const list = usePlacements({
    state: state === "all" ? undefined : state, branch, carrier: get("carrier") || undefined, owner: get("owner") || undefined, approved: get("approved") || undefined,
    period: get("month") || undefined, page, page_size: pageSize, q: q.trim() || undefined,
  });
  const canEdit = can(me, "budget");
  const s = list.data?.summary;
  const hasFilters = FILTER_KEYS.some((k) => (k === "state" ? state !== "all" : !!get(k))) || q.trim() !== "";

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  }

  function clearFilters() {
    const next = new URLSearchParams(params);
    FILTER_KEYS.forEach((k) => next.delete(k));
    next.delete("page");
    setParams(next, { replace: true });
    setQ("");
  }

  async function setStatus(p: PlacementFee, status: "not_paid" | "pending") {
    try {
      await update.mutateAsync({ id: p.id, version: p.version, fee_status: status });
      toast.success(t("placements.saved", { name: p.driver.full_name }));
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
  }

  async function writeOff(p: PlacementFee) {
    const ok = await confirmDialog({ title: t("placements.writeOffTitle", { name: p.driver.full_name }), description: t("placements.writeOffDesc"), confirmLabel: t("placements.writeOff"), danger: true });
    if (ok) await setStatus(p, "not_paid");
  }

  async function doVoid(p: PlacementFee, paymentId: number) {
    const r = p.received.find((x) => x.id === paymentId);
    const ok = await confirmDialog({
      title: t("placements.voidTitle"),
      description: t("placements.voidDesc", { amount: r ? fmt(r.amount, r.currency) : "", account: r?.account ?? "" }),
      confirmLabel: t("placements.void"),
      danger: true,
    });
    if (!ok) return;
    try {
      await voidPayment.mutateAsync({ id: p.id, txId: paymentId, version: p.version });
      toast.success(t("placements.voided", { name: p.driver.full_name }));
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
  }

  const menuFor = (p: PlacementFee) =>
    placementMenu(
      p,
      canEdit,
      {
        navigate: (path) => navigate(path),
        addPayment: () => setPaying(toFeeTarget(p)),
        edit: () => setEditing(toFeeTarget(p)),
        writeOff: () => void writeOff(p),
        reopen: () => void setStatus(p, "pending"),
        voidPayment: (id) => void doVoid(p, id),
        copy: (text) => void copyText(text),
        profile: personNav,
      },
      tm,
    );

  function openMenu(e: MouseEvent<HTMLButtonElement>) {
    // Qatorning o'ng tugma menyusini chap tugma bilan ochadi (bir xil amallar, bir joyda)
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.dispatchEvent(new window.MouseEvent("contextmenu", { bubbles: true, cancelable: true, clientX: r.left, clientY: r.bottom + 4 }));
  }

  return (
    <BudgetLayout showPeriod={false} subtitle={me ? t("placements.subtitle", { scope: scopeLabel(me, branch, branches.data ?? []) }) : undefined}>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard onClick={() => setParam("state", "overdue")} info={ti("placements.overdue")} label={t("placements.kpi.overdue")} value={s?.overdue ?? "—"} sub={t("placements.kpi.overdueSub")} subTone={s && s.overdue > 0 ? "danger" : "muted"} />
        <KpiCard onClick={() => setParam("state", "due")} info={ti("placements.due")} label={t("placements.kpi.due")} value={s?.due ?? "—"} sub={s && money(s.expected_sum) > 0 ? t("placements.kpi.expected", { amount: formatMoney(money(s.expected_sum)) }) : t("placements.kpi.dueSub")} />
        <KpiCard onClick={() => setParam("state", "paid")} info={ti("placements.paid")} label={t("placements.kpi.paid")} value={s ? s.paid + s.partial : "—"} sub={s && money(s.paid_sum) > 0 ? formatMoney(money(s.paid_sum)) : s && s.partial ? t("placements.kpi.partial", { count: s.partial }) : undefined} subTone="success" />
        <KpiCard onClick={() => setParam("state", "not_paid")} info={ti("placements.notPaid")} label={t("placements.kpi.notPaid")} value={s?.not_paid ?? "—"} />
        <KpiCard onClick={() => setParam("approved", "no")} info={ti("placements.approvalPending")} label={t("placements.kpi.approvalPending")} value={s?.approval_pending ?? "—"} sub={t("placements.kpi.approvalPendingSub")} subTone={s && s.approval_pending > 0 ? "danger" : "muted"} />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={state} emptyValue="all" onChange={(e) => setParam("state", e.target.value === "all" ? null : e.target.value)} className="w-auto min-w-[200px] max-w-[300px]" aria-label={t("placements.status")}>
          {STATES.map((st) => (
            <option key={st} value={st}>
              {t(`placements.state.${st}`)}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={get("carrier")} onChange={(e) => setParam("carrier", e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("placements.filters.carrier")}>
          <option value="">{t("placements.filters.carrier")}</option>
          {(carriers.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={get("owner")} onChange={(e) => setParam("owner", e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("placements.filters.recruiter")}>
          <option value="">{t("placements.filters.recruiter")}</option>
          {(owners.data?.results ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={get("approved")} onChange={(e) => setParam("approved", e.target.value)} className="w-auto min-w-[170px]" aria-label={t("placements.filters.approval")}>
          <option value="">{t("placements.filters.approval")}</option>
          <option value="yes">{t("placements.filters.approvalYes")}</option>
          <option value="no">{t("placements.filters.approvalNo")}</option>
        </FilterSelect>
        <FilterSelect value={get("month")} onChange={(e) => setParam("month", e.target.value)} className="w-auto min-w-[160px]" aria-label={t("placements.filters.period")}>
          <option value="">{t("placements.filters.period")}</option>
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </FilterSelect>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("placements.search")} className="w-[220px]" aria-label={t("placements.search")} />
        {hasFilters && (
          <Button variant="link" onClick={clearFilters}>
            {tc("actions.clearFilters")}
          </Button>
        )}
        <span className="ml-auto text-[12px] text-muted">{t("placements.rule", { days: settings.data?.placement_fee_due_days ?? 14 })}</span>
      </div>

      {list.isPending ? (
        <PageLoading />
      ) : list.isError ? (
        <ErrorState message={list.error.message} onRetry={() => list.refetch()} />
      ) : list.data.results.length === 0 ? (
        <EmptyState title={t("placements.empty")} description={hasFilters ? t("placements.emptyFiltered") : t("placements.emptyDesc")} />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>{t("placements.cols.driver")}</Th>
                <Th>{t("placements.cols.recruiter")}</Th>
                {showBranchColumn && <Th>{t("placements.cols.branch")}</Th>}
                <Th>{t("placements.cols.carrier")}</Th>
                <Th>{t("placements.cols.started")}</Th>
                <Th>{t("placements.cols.due")}</Th>
                <Th className="text-right">{t("placements.cols.amount")}</Th>
                <Th>{t("placements.cols.status")}</Th>
                <Th>{t("placements.cols.note")}</Th>
                <Th className="w-[150px]" />
              </tr>
            </thead>
            <tbody>
              {list.data.results.map((p) => {
                const received = money(p.received_total);
                const remaining = p.remaining !== null ? money(p.remaining) : null;
                const receivable = canEdit && canReceive(p.fee_status, p.approved);
                return (
                  <ContextMenu key={p.id} items={() => menuFor(p)}>
                    <Tr>
                      <Td className="font-semibold">
                        <Link to={`/drivers/${p.driver.id}`} className="hover:underline">
                          {p.driver.full_name}
                        </Link>
                      </Td>
                      <Td>{p.owner ? <PersonLink id={p.owner.id} name={p.owner.display_name} /> : <span className="text-muted">—</span>}</Td>
                      {showBranchColumn && <Td>{p.branch_name ?? "—"}</Td>}
                      <Td>
                        <Link to={`/carriers/${p.carrier.id}`} className="hover:underline">
                          {p.carrier.name}
                        </Link>
                      </Td>
                      <Td className="whitespace-nowrap">{formatDate(p.start_date)}</Td>
                      <Td className="whitespace-nowrap">{formatDate(p.fee_due_date)}</Td>
                      <Td className="text-right whitespace-nowrap">
                        {p.fee_amount !== null ? formatMoney(money(p.fee_amount)) : <span className="text-muted">—</span>}
                        {received > 0 && (
                          <div className="text-[11px] text-muted">
                            {t("placements.receivedLine", { received: formatMoneyExact(received) })}
                            {remaining !== null && remaining > 0 ? ` · ${t("placements.remainingLine", { remaining: formatMoneyExact(remaining) })}` : ""}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <FeeBadge status={p.fee_status} due={p.fee_due_date} paidAt={p.fee_paid_at} approved={p.approved} onClick={canEdit ? () => (receivable ? setPaying(toFeeTarget(p)) : setEditing(toFeeTarget(p))) : undefined} />
                        {p.legacy && <div className="mt-0.5 text-[11px] text-muted">{t("placements.legacy")}</div>}
                        {p.received.length > 0 && (
                          <div className="mt-0.5 text-[11px] text-muted">{p.received
                              .map((r) => `${fmt(r.amount, r.currency)}${r.commission ? ` (${t("commission.rowIn", { base: fmt(r.base_amount, r.currency), pct: Number(r.commission_pct) })})` : ""} → ${r.account} · ${whenText(r)}`)
                              .join(", ")}</div>
                        )}
                      </Td>
                      <Td className="max-w-[200px] truncate text-[12px] text-muted" title={p.fee_note}>
                        {p.fee_note || "—"}
                      </Td>
                      <Td className="text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {receivable && (
                            <Button size="sm" onClick={() => setPaying(toFeeTarget(p))}>
                              <Plus size={12} /> {t("placements.addPayment")}
                            </Button>
                          )}
                          <button type="button" aria-label={tc("actions.more")} title={tc("actions.more")} onClick={openMenu} className="grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-fill hover:text-ink">
                            <MoreHorizontal size={16} aria-hidden />
                          </button>
                        </span>
                      </Td>
                    </Tr>
                  </ContextMenu>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            page={page}
            pages={list.data.pages}
            count={list.data.count}
            pageSize={pageSize}
            onPage={(n) => setParam("page", String(n))}
            onPageSize={(s) => {
              rememberPageSize("placements", s);
              setParam("page_size", String(s));
            }} label={t("placements.unit")} />
        </>
      )}
      <PaymentModal target={paying} onOpenChange={(o) => !o && setPaying(null)} />
      <FeeEditModal target={editing} onOpenChange={(o) => !o && setEditing(null)} />
    </BudgetLayout>
  );
}
